import { findInquiryFormByName, getTermBySlot, submitInquiry, type InquiryField, type InquiryForm } from './api';

const FORM_NAME = '제휴 및 문의';
const INQUIRY_TERM_SLOT = 'inquiry_required';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fieldInput(field: InquiryField): string {
  const required = field.required ? 'required' : '';
  const name = escapeHtml(field.key);
  const label = escapeHtml(field.label);

  if (field.type === 'textarea') {
    return `<textarea id="inquiry-${name}" name="${name}" rows="6" ${required}></textarea>`;
  }

  if (field.type === 'select') {
    const options = (field.options ?? [])
      .map((option) => `<option value="${escapeHtml(option)}">${escapeHtml(option)}</option>`)
      .join('');
    return `
      <select id="inquiry-${name}" name="${name}" ${required}>
        <option value="">선택하세요</option>
        ${options}
      </select>
    `;
  }

  if (field.type === 'checkbox') {
    return `
      <label class="inquiry-form__check">
        <input type="checkbox" id="inquiry-${name}" name="${name}" value="true" ${required} />
        <span>${label}</span>
      </label>
    `;
  }

  const inputType = field.type === 'tel' ? 'tel' : field.type === 'email' ? 'email' : field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text';
  return `<input id="inquiry-${name}" type="${inputType}" name="${name}" ${required} />`;
}

function renderForm(form: InquiryForm, termTitle: string, termContent: string, termId: number): string {
  const fields = form.fields
    .map((field) => {
      const requiredMark = field.required ? ' <span class="inquiry-form__required">*</span>' : '';
      if (field.type === 'checkbox') {
        return `<div class="inquiry-form__field">${fieldInput(field)}</div>`;
      }
      return `
        <div class="inquiry-form__field">
          <label for="inquiry-${escapeHtml(field.key)}">${escapeHtml(field.label)}${requiredMark}</label>
          ${fieldInput(field)}
        </div>
      `;
    })
    .join('');

  const submitLabel = form.submit_button_label?.trim() || '문의하기';

  return `
    <form class="inquiry-form" data-inquiry-form novalidate>
      ${form.description ? `<p class="inquiry-form__desc">${escapeHtml(form.description)}</p>` : ''}
      ${fields}
      <div class="inquiry-form__agree-wrap">
        <label class="inquiry-form__agree">
          <input type="checkbox" name="inquiry_agree" required />
          <span>${escapeHtml(termTitle)}</span>
        </label>
        <button type="button" class="inquiry-form__term-toggle" data-term-toggle>내용 보기</button>
        <div class="inquiry-form__term" data-term-body hidden>${termContent}</div>
        <input type="hidden" name="term_id" value="${termId}" />
      </div>
      <button type="submit" class="inquiry-form__submit">${escapeHtml(submitLabel)}</button>
      <p class="inquiry-form__status" data-inquiry-status role="status"></p>
    </form>
  `;
}

function normalizePhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return value.trim();
}

function sanitizeName(value: string): string {
  return value.replace(/[^가-힣a-zA-Z0-9()\-_,.#\s]/g, '').trim().slice(0, 25);
}

export function initInquiryForm(root: HTMLElement): () => void {
  const mountEl = root.querySelector<HTMLElement>('[data-inquiry-mount]');
  if (!mountEl) return () => {};

  let disposed = false;
  mountEl.innerHTML = `<p class="inquiry-form__status">문의 양식을 불러오는 중입니다.</p>`;

  void (async () => {
    try {
      const [form, term] = await Promise.all([
        findInquiryFormByName(FORM_NAME),
        getTermBySlot(INQUIRY_TERM_SLOT),
      ]);

      if (disposed) return;

      if (!form) {
        mountEl.innerHTML = `<p class="inquiry-form__status is-error">제휴 및 문의 양식을 찾을 수 없습니다.</p>`;
        return;
      }

      if (!term) {
        mountEl.innerHTML = `<p class="inquiry-form__status is-error">문의 약관을 찾을 수 없습니다.</p>`;
        return;
      }

      mountEl.innerHTML = renderForm(form, term.title, term.content, term.id);
      bindForm(mountEl, form, term.id);
    } catch {
      if (!disposed) {
        mountEl.innerHTML = `<p class="inquiry-form__status is-error">문의 양식을 불러오지 못했습니다.</p>`;
      }
    }
  })();

  return () => {
    disposed = true;
  };
}

function bindForm(root: HTMLElement, form: InquiryForm, termId: number) {
  const formEl = root.querySelector<HTMLFormElement>('[data-inquiry-form]');
  const status = root.querySelector<HTMLElement>('[data-inquiry-status]');
  const toggle = root.querySelector<HTMLButtonElement>('[data-term-toggle]');
  const termBody = root.querySelector<HTMLElement>('[data-term-body]');
  if (!formEl) return;

  toggle?.addEventListener('click', () => {
    if (!termBody) return;
    const hidden = termBody.hasAttribute('hidden');
    termBody.toggleAttribute('hidden', !hidden);
    toggle.textContent = hidden ? '내용 닫기' : '내용 보기';
  });

  formEl.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!status) return;

    const data = new FormData(formEl);
    const agree = data.get('inquiry_agree') === 'on';
    if (!agree) {
      status.className = 'inquiry-form__status is-error';
      status.textContent = '개인정보 수집·이용에 동의해 주세요.';
      return;
    }

    const formData: Record<string, unknown> = {};
    form.fields.forEach((field) => {
      const raw = data.get(field.key);
      if (field.type === 'checkbox') {
        formData[field.key] = raw === 'true' || raw === 'on';
        return;
      }
      formData[field.key] = typeof raw === 'string' ? raw.trim() : '';
    });

    const name = sanitizeName(String(formData.name ?? data.get('name') ?? ''));
    if (!name) {
      status.className = 'inquiry-form__status is-error';
      status.textContent = '이름을 입력해 주세요.';
      return;
    }

    const emailRaw = String(formData.email ?? '').trim();
    const phoneRaw = String(formData.phone ?? '').trim();
    const contentRaw = String(formData.content ?? '').trim();

    const submitBtn = formEl.querySelector<HTMLButtonElement>('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;
    status.className = 'inquiry-form__status';
    status.textContent = '전송 중입니다.';

    try {
      await submitInquiry({
        form_id: form.id,
        name,
        email: emailRaw || null,
        phone: phoneRaw ? normalizePhone(phoneRaw) : null,
        content: contentRaw || null,
        form_data: {
          ...formData,
          name,
          email: emailRaw,
          phone: phoneRaw ? normalizePhone(phoneRaw) : phoneRaw,
          content: contentRaw,
        },
        agreements: [{ term_id: termId, agreed: true }],
      });
      formEl.reset();
      status.className = 'inquiry-form__status is-success';
      status.textContent = '문의가 접수되었습니다. 빠르게 연락드리겠습니다.';
    } catch {
      status.className = 'inquiry-form__status is-error';
      status.textContent = '문의 접수에 실패했습니다. 잠시 후 다시 시도해 주세요.';
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });
}
